import apiClient from "../api/axios";
import alertService from "../components/alert/alertService";
import { handleApiError } from "../api/handleApiError";
import {
  EconomicIndicator,
  ResponseCreateEconomicIndicators,
} from "../models/dto/response/ResponseCreateEconomicIndicators";
import {
  EcoEffActualRequest,
  EcoEfficiency,
  EcoEfficiencyPlanResponse,
  EcoEfficiencyPlanResults,
  EcoEfficiencyResponse,
  EcoEfficiencyResponseData,
  EconomicIndicatorValueResponse,
  EconomicIndicatorValueResult,
} from "../models/dto/response/ResponseEcoEfficiency";
import { BaseYearResponse } from "../models/BaseYear";
import { EnvironmentalImpactTypesResponse } from "../models/EnvironmentalImpactTypes";
import {
  ComponentYearEntry,
  CustomComponentRequest,
  EcoEffDashboard,
  EcoEfficiencyPreCalculationResponse,
  EconomicIndicatorComponentValueResponse,
  PercentChangeRequest,
  PreCalculationResults,
} from "../models/EcoEfficiency";
import { AxiosResponse } from "axios";

export const EcoEfficiencyService = {
  recalculateEcoEfficiency: async (
    refId: string
  ): Promise<EcoEfficiency[] | null> => {
    try {
      alertService.showLoading();
      const response = await apiClient.patch(
        `/v2/eco-efficiency/${refId}/recalculate`
      );
      const temp: EcoEfficiencyResponse = response.data;
      return temp.results.ecoEfficiencies;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
  updateEcoEfficiency: async (
    refId: string,
    form: any
  ): Promise<EcoEfficiencyResponseData | null> => {
    try {
      alertService.showLoading();
      const response: AxiosResponse<EcoEfficiencyResponse> =
        await apiClient.patch(`/v2/eco-efficiency/${refId}`, form);
      return response.data.results;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
  deleteEcoEfficiency: async (
    refId: string
  ): Promise<EcoEfficiency[] | null> => {
    try {
      alertService.showLoading();
      const response = await apiClient.delete(`/v2/eco-efficiency/${refId}`);
      const temp: EcoEfficiencyResponse = response.data;
      return temp.results.ecoEfficiencies;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
  createEcoEfficiency: async (
    form: any
  ): Promise<EcoEfficiencyResponseData | null> => {
    try {
      alertService.showLoading();
      const response: AxiosResponse<EcoEfficiencyResponse> =
        await apiClient.post("/v2/eco-efficiency", form);
      return response.data.results;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
  getEnvironmentalImpactTypes: async (): Promise<string[] | null> => {
    try {
      alertService.showLoading();
      const response = await apiClient.get(
        "/v1/environmental-impact-types/options"
      );
      const temp: EnvironmentalImpactTypesResponse = response.data;
      return temp.results.type;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
  getBaseYear: async (
    refId: string,
    startMonth: number,
    startYear: number,
    suborganizationRef?: string
  ): Promise<number[] | null> => {
    const body: any = {
      indicatorRef: refId,
      startMonth: startMonth,
      startYear: startYear,
    };

    if (suborganizationRef) body.suborganizationRef = suborganizationRef;

    try {
      const response = await apiClient.post("/v2/base-years/options", body);
      const temp: BaseYearResponse = response.data;
      return temp.results.years;
    } catch (error: any) {
      // silent — base year options failure is non-blocking
    }
    return null;
  },
  getEcoEfficiency: async (): Promise<EcoEfficiency[] | null> => {
    try {
      const response = await apiClient.get("/v2/eco-efficiency");
      const temp: EcoEfficiencyResponse = response.data;
      return temp.results.ecoEfficiencies;
    } catch (error: any) {
      handleError(error);
    }
    return null;
  },

  getEconomicIndicators: async (): Promise<EconomicIndicator[] | null> => {
    try {
      const response = await apiClient.get("/v1/economic-indicators");
      const temp: ResponseCreateEconomicIndicators = response.data;
      return temp.results.economicIndicators;
    } catch (error: any) {
      handleError(error);
    }
    return null;
  },
  createEconomicIndicators: async (
    formData: any
  ): Promise<EconomicIndicator[] | null> => {
    try {
      alertService.showLoading();
      const response = await apiClient.post(
        "/v1/economic-indicators",
        formData
      );
      const temp: ResponseCreateEconomicIndicators = response.data;
      return temp.results.economicIndicators;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
  editEconomicIndicators: async (
    refId: string,
    formData: any
  ): Promise<EconomicIndicator[] | null> => {
    try {
      alertService.showLoading();
      const response = await apiClient.patch(
        `/v1/economic-indicators/${refId}`,
        formData
      );
      const temp: ResponseCreateEconomicIndicators = response.data;
      return temp.results.economicIndicators;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
  enableDisableEconomicIndicators: async (
    refId: string
  ): Promise<EconomicIndicator[] | null> => {
    try {
      const response = await apiClient.patch(
        `/v1/economic-indicators/${refId}/enable-disable`
      );
      const temp: ResponseCreateEconomicIndicators = response.data;
      return temp.results.economicIndicators;
    } catch (error: any) {
      handleError(error);
    }
    return null;
  },
  getEcoEfficiencyDashboard: async (
    startDate: string,
    endDate: string,
    indicatorRef: string,
    suborganizationRefs: string[],
    compareOffsetYears: number
  ): Promise<EcoEffDashboard | null> => {
    try {
      alertService.showLoading();
      const response = await apiClient.post(`/v2/eco-efficiency/dashboard`, {
        startDate,
        endDate,
        indicatorRef,
        suborganizationRefs,
        compareOffsetYears,
      });
      return response.data.results;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
  updateEcoefficiencyActual: async (
    form: EcoEffActualRequest,
    refId: string
  ): Promise<ComponentYearEntry[] | null> => {
    try {
      const response: AxiosResponse<EconomicIndicatorComponentValueResponse> =
        await apiClient.post(
          `/v2/economic-indicators/components/${refId}`,
          form
        );
      return response.data.results.success;
    } catch (error: any) {
      handleError(error);
    }
    return null;
  },
  getEcoefficiencyActuals: async (
    refId: string
  ): Promise<EconomicIndicatorValueResult | null> => {
    try {
      const response: AxiosResponse<EconomicIndicatorValueResponse> =
        await apiClient.get(`v2/eco-efficiency/actuals/${refId}`);
      return response.data.results;
    } catch (error: any) {
      handleError(error);
    }
    return null;
  },

  getEcoEfficienciesPlan: async (
    ecoEfficienciesRefId: string
  ): Promise<EcoEfficiencyPlanResults | null> => {
    try {
      alertService.showLoading();
      const response: AxiosResponse<EcoEfficiencyPlanResponse> =
        await apiClient.get(`v2/eco-efficiency/plans/${ecoEfficienciesRefId}`);
      return response.data.results;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
  calEcoEfficiencyPlans: async (
    ecoEfficienciesRefId: string,
    body: PercentChangeRequest
  ): Promise<PreCalculationResults | null> => {
    try {
      const response: AxiosResponse<EcoEfficiencyPreCalculationResponse> =
        await apiClient.post(
          `v2/eco-efficiency/plans/cal/${ecoEfficienciesRefId}`,
          body
        );
      return response.data.results;
    } catch (error: any) {
      handleError(error);
    }
    return null;
  },
  crateEcoEfficiencyPlans: async (
    ecoEfficienciesRefId: string,
    body: CustomComponentRequest
  ): Promise<any | null> => {
    try {
      alertService.showLoading();
      const response: AxiosResponse<any> = await apiClient.post(
        `/v2/eco-efficiency/plans/${ecoEfficienciesRefId}`,
        body
      );
      return response.data.results;
    } catch (error: any) {
      handleError(error);
    } finally {
      alertService.closeLoading();
    }
    return null;
  },
};

const handleError = (error: unknown) =>
  handleApiError(
    error,
    {
      VALIDATION_ERROR: {
        messageKey: (body) =>
          body.includes("Duplicate") ? "Component-Name-Duplicated" : "validation-failed",
        type: "error",
      },
      ECONOMIC_INDICATOR_ALREADY_EXISTS: "Economic-Indicator-Already-Exists",
      ENVIRONMENTAL_IMPACT_TYPES_RETRIEVE_FAILED:
        "Environmental-Impact-Types-Retrieve-Failed",
      BASE_YEAR_OPTIONS_RETRIEVE_FAILED: "Base-Year-Options-Retrieve-Failed",
      ECO_EFFICIENCY_ALREADY_EXISTS: "Eco-Efficiency-Already-Exists",
      FORBIDDEN_ACTION_ERROR: "Forbidden-Action-Error",
      FORBIDDEN_ECO_EFFICIENCY_ERROR: "Action-Not-Allowed-Upgrade-Required",
      FORBIDDEN_ECONOMIC_INDICATOR_ERROR: "Action-Not-Allowed-Upgrade-Required",
    },
  );
